import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

const ThemeContext = createContext(null);
const STORAGE_KEY = "gg_theme"; // 'light' | 'dark' | 'system'

/** Read the OS-level preference. */
function readSystemPref() {
    if (typeof window === "undefined") return "light";
    return window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}

/** Apply the resolved mode to the <html> element. */
function applyMode(mode) {
    const root = document.documentElement;
    if (mode === "dark") root.classList.add("dark");
    else root.classList.remove("dark");
    root.style.colorScheme = mode; // hints native form controls & scrollbars
}

export function ThemeProvider({ children }) {
    // preference: what user selected — 'light' | 'dark' | 'system'
    const [preference, setPreference] = useState(() => {
        if (typeof window === "undefined") return "system";
        return localStorage.getItem(STORAGE_KEY) || "system";
    });

    // mode: the resolved value actually applied — 'light' | 'dark'
    const [mode, setMode] = useState(() => {
        const p = typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
        return p && p !== "system" ? p : readSystemPref();
    });

    // Apply on mount and whenever preference resolves to a new mode
    useEffect(() => {
        const resolved = preference === "system" ? readSystemPref() : preference;
        setMode(resolved);
        applyMode(resolved);
    }, [preference]);

    // Track OS changes ONLY while the user is on 'system' preference
    useEffect(() => {
        if (preference !== "system" || typeof window === "undefined" || !window.matchMedia) return;
        const mq = window.matchMedia("(prefers-color-scheme: dark)");
        const handler = (e) => {
            const resolved = e.matches ? "dark" : "light";
            setMode(resolved);
            applyMode(resolved);
        };
        // some older browsers use addListener
        if (mq.addEventListener) mq.addEventListener("change", handler);
        else mq.addListener(handler);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", handler);
            else mq.removeListener(handler);
        };
    }, [preference]);

    const setPref = useCallback((next) => {
        setPreference(next);
        if (next === "system") localStorage.removeItem(STORAGE_KEY);
        else localStorage.setItem(STORAGE_KEY, next);
    }, []);

    const toggle = useCallback(() => {
        setPref(mode === "dark" ? "light" : "dark");
    }, [mode, setPref]);

    const value = useMemo(
        () => ({ mode, preference, setPref, toggle }),
        [mode, preference, setPref, toggle]
    );

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
    return ctx;
}
